/**
 * MENORA LABS · Interacciones propias sobre Horizon.
 * - Añadir al carrito con el evento estándar `cart:lines-update` que escucha Horizon
 *   (así el carrito lateral, el contador y la sección del carrito se actualizan solos).
 * - Galería, carruseles, barra fija móvil, cuenta atrás, reseñas y pie de página.
 * Sin dependencias externas. Desplazamiento nativo (scroll-snap) para no romper el gesto táctil.
 */

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const scrollBehavior = () => (prefersReducedMotion() ? 'auto' : 'smooth');

/* ------------------------------------------------------------------ */
/* Carrito                                                             */
/* ------------------------------------------------------------------ */

async function loadShopifyEvents() {
  try {
    return await import('@shopify/events');
  } catch (error) {
    console.warn('[menora] @shopify/events no disponible; se usará recarga del carrito.', error);
    return null;
  }
}

/**
 * Añade una línea al carrito y notifica a Horizon.
 * @param {HTMLElement} source
 * @param {string} variantId
 * @param {number} quantity
 */
async function addToCart(source, variantId, quantity) {
  const routes = window.Theme?.routes || {};
  const addUrl = routes.cart_add_url || '/cart/add.js';
  const cartUrl = `${routes.cart_url || '/cart'}.js`;

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  const events = await loadShopifyEvents();
  const CartLinesUpdateEvent = events?.CartLinesUpdateEvent;
  let deferred = null;

  if (CartLinesUpdateEvent) {
    deferred = CartLinesUpdateEvent.createPromise();
    source.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity }],
        promise: deferred.promise,
      })
    );
  }

  const body = new FormData();
  body.append('id', variantId);
  body.append('quantity', String(quantity));
  if (sectionIds.length) body.append('sections', sectionIds.join(','));

  let response;
  try {
    const res = await fetch(addUrl, {
      method: 'POST',
      body,
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
    });
    response = await res.json();
  } catch (error) {
    deferred?.reject(error);
    throw error;
  }

  const cart = await fetch(cartUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then((r) =>
    r.json()
  );

  const didError = Boolean(response?.status);
  if (deferred && CartLinesUpdateEvent) {
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'menora-product',
        sourceId: source.id || 'menora-product',
        itemCount: quantity,
        sections: didError ? undefined : response.sections,
        didError,
      },
    });
  }

  if (didError) throw new Error(response.description || response.message || 'No se pudo añadir al carrito');

  // Sin Horizon (p. ej. si falla la carga del módulo): ir al carrito.
  if (!CartLinesUpdateEvent || !document.querySelector('cart-drawer-component')) {
    window.location.href = routes.cart_url || '/cart';
  }
  return cart;
}

/**
 * «Mejorar mi oferta» del carrito lateral: cambia el paquete suelto por el pack.
 * Primero añade el pack y después retira el paquete suelto, para no dejar nunca el carrito vacío.
 * Los importes los calcula Shopify; Horizon repinta el carrito con las secciones devueltas.
 * @param {HTMLElement} source
 * @param {string} fromKey - Clave de la línea del paquete suelto
 * @param {string} toVariantId - Variante del pack (bundle)
 */
async function upgradeCartLine(source, fromKey, toVariantId) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  const events = await loadShopifyEvents();
  const CartLinesUpdateEvent = events?.CartLinesUpdateEvent;
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };

  const addRes = await fetch('/cart/add.js', {
    method: 'POST',
    headers,
    body: JSON.stringify({ items: [{ id: Number(toVariantId), quantity: 1 }] }),
  });
  const added = await addRes.json();
  if (!addRes.ok || added.status) throw new Error(added.description || added.message || 'No se pudo añadir el pack');

  // Retira el paquete suelto pidiendo el HTML del carrito. Si Shopify rechaza la petición de secciones
  // (p. ej. una ruta no válida), se reintenta sin secciones: lo importante es no dejar ambos productos.
  const change = async (withSections) => {
    const body = { id: fromKey, quantity: 0 };
    const path = window.location.pathname;
    if (withSections && sectionIds.length && path.startsWith('/')) {
      Object.assign(body, { sections: sectionIds.join(','), sections_url: path });
    }
    const res = await fetch('/cart/change.js', { method: 'POST', headers, body: JSON.stringify(body) });
    const json = await res.json();
    return { ok: res.ok && !json.status && !json.errors, json };
  };
  let removal = await change(true);
  if (!removal.ok) removal = await change(false);
  const cart = removal.json;
  const removeFailed = !removal.ok;

  if (!CartLinesUpdateEvent) {
    window.location.reload();
    return;
  }
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [{ id: fromKey, quantity: 0 }],
      promise: deferred.promise,
    })
  );
  // Si no se pudo retirar el paquete suelto, sin secciones: Horizon vuelve a pedir el carrito real.
  deferred.resolve({
    cart: removeFailed ? undefined : CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      sections: removeFailed ? undefined : cart.sections,
      items: removeFailed ? undefined : cart.items,
      itemCount: removeFailed ? undefined : cart.item_count,
      source: 'menora-upgrade',
      didError: Boolean(removeFailed),
    },
  });
  if (removeFailed) throw new Error(cart.description || cart.message || 'No se pudo retirar el paquete suelto');
}

document.addEventListener('click', async (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-mn-upgrade]') : null;
  if (!(btn instanceof HTMLButtonElement) || btn.dataset.busy === 'true') return;
  btn.dataset.busy = 'true';
  btn.disabled = true;
  btn.classList.add('is-loading');
  const msg = btn.parentElement?.querySelector('[data-mn-upgrade-msg]');
  try {
    await upgradeCartLine(btn, btn.dataset.fromKey || '', btn.dataset.toVariant || '');
  } catch (error) {
    console.warn('[menora] Mejorar oferta:', error);
    if (msg instanceof HTMLElement) {
      msg.textContent = 'No se pudo mejorar la oferta. Revisa tu carrito e inténtalo de nuevo.';
      msg.hidden = false;
    }
    btn.disabled = false;
    btn.dataset.busy = 'false';
    btn.classList.remove('is-loading');
  }
});

/* ------------------------------------------------------------------ */
/* Producto (galería + paquetes + botón + barra fija)                  */
/* ------------------------------------------------------------------ */

class MenoraProduct extends HTMLElement {
  connectedCallback() {
    this.packs = [...this.querySelectorAll('.mn-pack__input')];
    this.atc = this.querySelector('[data-mn-atc]');
    this.atcLabel = this.querySelector('[data-mn-atc-label]');
    this.message = this.querySelector('[data-mn-atc-message]');
    this.sticky = this.querySelector('[data-mn-sticky]');
    this.stickyPrice = this.querySelector('[data-mn-sticky-price]');
    this.stickyBtn = this.querySelector('[data-mn-sticky-btn]');

    this.packs.forEach((input) => input.addEventListener('change', () => this.onPackChange()));
    this.atc?.addEventListener('click', () => this.onAdd(this.atc));
    this.stickyBtn?.addEventListener('click', () => this.onAdd(this.stickyBtn));

    this.onPackChange();
    this.setupStockBar();
    this.setupGallery();
    this.setupSticky();
  }

  get selected() {
    return this.packs.find((p) => p.checked) || this.packs[0];
  }

  onPackChange() {
    this.packs.forEach((input) =>
      input.closest('.mn-pack, .mn-offer-card')?.classList.toggle('is-selected', input.checked)
    );
    const sel = this.selected;
    if (this.stickyPrice && sel) this.stickyPrice.textContent = sel.dataset.price || '';
    if (this.message) this.message.hidden = true;
    if (!sel) return;

    // Resumen superior y botón: precio y precio anterior del paquete elegido.
    const price = sel.dataset.price || '';
    const compare = sel.dataset.compare || '';
    const set = (selector, text) => {
      const el = this.querySelector(selector);
      if (el) el.textContent = text;
    };
    set('[data-mn-sum-price]', price);
    set('[data-mn-sum-compare]', compare);
    set('[data-mn-atc-price]', price);
    set('[data-mn-atc-compare]', compare);
  }

  setupStockBar() {
    const bar = this.querySelector('[data-mn-stock]');
    if (!bar) return;
    // Animación discreta al entrar en pantalla (una sola vez).
    if (!('IntersectionObserver' in window) || prefersReducedMotion()) {
      bar.classList.add('is-in');
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          bar.classList.add('is-in');
          io.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    io.observe(bar);
    setTimeout(() => bar.classList.add('is-in'), 2500);
  }

  showMessage(text, info = false) {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.classList.toggle('is-info', info);
    this.message.hidden = false;
  }

  async onAdd(button) {
    const sel = this.selected;
    if (!sel) return;
    const variantId = sel.dataset.variantId;
    const quantity = parseInt(sel.dataset.quantity || '1', 10) || 1;

    if (!variantId) {
      // Sin producto en Shopify no se simula ninguna compra: se avisa junto al selector.
      if (button === this.stickyBtn) {
        this.querySelector('[data-mn-packs]')?.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
      }
      this.showMessage('Vista previa: este paquete todavía no tiene su variante con precio en Shopify. No se añade nada al carrito.', true);
      return;
    }
    if (sel.dataset.available === 'false') {
      this.showMessage('Este paquete no está disponible en este momento.');
      return;
    }

    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    try {
      await addToCart(this, variantId, quantity);
      if (this.message) this.message.hidden = true;
    } catch (error) {
      this.showMessage(error.message || 'No se pudo añadir al carrito. Inténtalo de nuevo.');
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  }

  setupGallery() {
    const track = this.querySelector('[data-mn-gallery-track]');
    const dotsWrap = this.querySelector('[data-mn-gallery-dots]');
    if (!track || !dotsWrap) return;
    const slides = [...track.children];
    if (slides.length < 2) return;

    const dots = slides.map((slide, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.tabIndex = -1;
      b.addEventListener('click', () => track.scrollTo({ left: slide.offsetLeft - track.offsetLeft - parseFloat(getComputedStyle(track).paddingLeft || '0'), behavior: scrollBehavior() }));
      dotsWrap.appendChild(b);
      return b;
    });

    const update = () => {
      const center = track.scrollLeft + track.clientWidth * 0.35;
      let index = 0;
      slides.forEach((s, i) => {
        if (s.offsetLeft - track.offsetLeft <= center) index = i;
      });
      dots.forEach((d, i) => d.setAttribute('aria-current', String(i === index)));
    };
    track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    update();
  }

  setupSticky() {
    if (!this.sticky || !this.atc) return;
    // La barra aparece cuando el botón principal ha quedado por encima de la pantalla
    // y se mantiene durante el resto del recorrido (igual que la referencia).
    const setVisible = (passed) => {
      if (this.sticky.classList.contains('is-visible') === passed) return;
      this.sticky.classList.toggle('is-visible', passed);
      this.sticky.setAttribute('aria-hidden', String(!passed));
      if (this.stickyBtn) this.stickyBtn.tabIndex = passed ? 0 : -1;
    };
    const check = () => {
      applyPreviewBarOffset();
      setVisible(this.atc.getBoundingClientRect().bottom < 0);
    };

    // Horizon desplaza `.page-wrapper` (no la ventana): se escucha el scroll de cualquier
    // contenedor en fase de captura, además del IntersectionObserver.
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        check();
      });
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    syncPreviewBarOffset();
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(() => check(), { threshold: [0, 1] }).observe(this.atc);
    }
    check();
  }
}

if (!customElements.get('menora-product')) customElements.define('menora-product', MenoraProduct);

/* ------------------------------------------------------------------ */
/* Barra de vista previa de Shopify                                    */
/* En modo preview, Shopify inyecta una barra fija abajo                */
/* (#PBarNextFrameWrapper) que tapa la barra de compra y se queda con   */
/* sus clics. Se sube la barra de compra justo por encima de ella.      */
/* Las clientas no ven esa barra, así que fuera de preview no cambia.   */
/* ------------------------------------------------------------------ */

let previewBarWatched = false;
function applyPreviewBarOffset() {
  const bar = document.getElementById('PBarNextFrameWrapper');
  let offset = 0;
  if (bar && getComputedStyle(bar).display !== 'none') {
    const r = bar.getBoundingClientRect();
    if (r.height > 0 && r.top < window.innerHeight) offset = Math.max(0, Math.round(window.innerHeight - r.top));
  }
  const value = `${offset}px`;
  if (document.documentElement.style.getPropertyValue('--mn-sticky-offset') !== value) {
    document.documentElement.style.setProperty('--mn-sticky-offset', value);
  }
}
function syncPreviewBarOffset() {
  const apply = () => {
    const bar = document.getElementById('PBarNextFrameWrapper');
    let offset = 0;
    if (bar && getComputedStyle(bar).display !== 'none') {
      const r = bar.getBoundingClientRect();
      if (r.height > 0 && r.top < window.innerHeight) offset = Math.max(0, Math.round(window.innerHeight - r.top));
    }
    document.documentElement.style.setProperty('--mn-sticky-offset', `${offset}px`);
  };
  apply();
  if (previewBarWatched) return;
  previewBarWatched = true;
  window.addEventListener('resize', apply, { passive: true });
  // La barra se inyecta tarde y cambia de tamaño al ocultarla ("Hide bar").
  new MutationObserver(apply).observe(document.body, { childList: true, subtree: false, attributes: true });
  const watchBar = () => {
    const bar = document.getElementById('PBarNextFrameWrapper');
    if (bar && 'ResizeObserver' in window) {
      new ResizeObserver(apply).observe(bar);
      new MutationObserver(apply).observe(bar, { attributes: true, subtree: true });
      return true;
    }
    return false;
  };
  if (!watchBar()) {
    const t = setInterval(() => watchBar() && clearInterval(t), 500);
    setTimeout(() => clearInterval(t), 15000);
  }
  // La barra de Shopify entra con animación: se recalcula unos segundos tras la carga.
  let n = 0;
  const settle = setInterval(() => {
    applyPreviewBarOffset();
    if (++n > 20) clearInterval(settle);
  }, 500);
}

/* ------------------------------------------------------------------ */
/* Carruseles genéricos ([data-mn-carousel])                           */
/* ------------------------------------------------------------------ */

function setupCarousel(root) {
  if (root.dataset.mnReady) return;
  root.dataset.mnReady = 'true';
  const track = root.querySelector('[data-mn-track]');
  if (!track) return;
  const items = [...track.children];
  const prev = root.querySelector('[data-mn-prev]');
  const next = root.querySelector('[data-mn-next]');
  const counter = root.querySelector('[data-mn-counter]');
  const dotsWrap = root.querySelector('[data-mn-dots]');

  const currentIndex = () => {
    const left = track.scrollLeft + 4;
    let index = 0;
    items.forEach((item, i) => {
      if (item.offsetLeft - track.offsetLeft - parseFloat(getComputedStyle(track).paddingLeft || '0') <= left) index = i;
    });
    // Al final del recorrido, marcar el último.
    if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 4) index = items.length - 1;
    return index;
  };

  const goTo = (i) => {
    const target = items[Math.max(0, Math.min(items.length - 1, i))];
    if (!target) return;
    const pad = parseFloat(getComputedStyle(track).paddingLeft || '0');
    track.scrollTo({ left: target.offsetLeft - track.offsetLeft - pad, behavior: scrollBehavior() });
  };

  let dots = [];
  if (dotsWrap && items.length > 1) {
    dots = items.map((_, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.tabIndex = -1;
      b.addEventListener('click', () => goTo(i));
      dotsWrap.appendChild(b);
      return b;
    });
  }

  const update = () => {
    const i = currentIndex();
    if (counter) counter.textContent = `${i + 1} / ${items.length}`;
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    dots.forEach((d, k) => d.setAttribute('aria-current', String(k === i)));
  };

  prev?.addEventListener('click', () => goTo(currentIndex() - 1));
  next?.addEventListener('click', () => goTo(currentIndex() + 1));
  track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
  window.addEventListener('resize', update, { passive: true });

  // Posición inicial (p. ej. opiniones: tarjeta 2 centrada, con las vecinas asomando a ambos lados).
  const start = parseInt(root.dataset.mnStart || '0', 10);
  if (start > 0 && items[start]) {
    const target = items[start];
    const left = 'mnCenter' in root.dataset
      ? target.offsetLeft - track.offsetLeft - (track.clientWidth - target.offsetWidth) / 2
      : target.offsetLeft - track.offsetLeft;
    track.scrollLeft = Math.max(0, left);
  }
  update();
}

/* ------------------------------------------------------------------ */
/* Cuenta atrás ([data-mn-countdown]) — solo con fecha real            */
/* ------------------------------------------------------------------ */

function setupCountdown(el) {
  if (el.dataset.mnReady) return;
  el.dataset.mnReady = 'true';
  const demo = el.dataset.demo === 'true';
  // Demostración: N horas desde la primera visita de la sesión. No se reinicia al recargar
  // ni al navegar durante la misma sesión; al llegar a cero se detiene en 0:00:00.
  let end = Date.parse(el.dataset.end || '');
  if (demo) {
    const hours = parseFloat(el.dataset.demoHours || '3') || 3;
    const key = `mn-demo-timer-end-${hours}`;
    let stored = NaN;
    try {
      stored = parseInt(sessionStorage.getItem(key) || '', 10);
    } catch (e) {}
    end = Number.isFinite(stored) ? stored : Date.now() + hours * 3600 * 1000;
    try {
      sessionStorage.setItem(key, String(end));
    } catch (e) {}
  }
  if (Number.isNaN(end)) return;
  const units = {
    d: el.querySelector('[data-unit="d"]'),
    h: el.querySelector('[data-unit="h"]'),
    m: el.querySelector('[data-unit="m"]'),
    s: el.querySelector('[data-unit="s"]'),
  };
  const dayBox = el.querySelector('[data-box="d"]');
  const daySep = el.querySelector('[data-sep="d"]');
  const pad = (n) => String(n).padStart(2, '0');
  const tick = () => {
    const diff = end - Date.now();
    if (diff <= 0) {
      // Fin: se queda en 0:00:00 (demostración) o desaparece (promoción real terminada).
      clearInterval(timer);
      if (demo) {
        units.h.textContent = '0';
        units.m.textContent = '00';
        units.s.textContent = '00';
        if (dayBox) dayBox.hidden = true;
        if (daySep) daySep.hidden = true;
        el.hidden = false;
        el.classList.add('is-finished');
      } else {
        el.hidden = true;
      }
      return;
    }
    const s = Math.floor(diff / 1000);
    const days = Math.floor(s / 86400);
    if (dayBox) dayBox.hidden = days === 0;
    if (daySep) daySep.hidden = days === 0;
    units.d.textContent = String(days);
    units.h.textContent = String(Math.floor((s % 86400) / 3600));
    units.m.textContent = pad(Math.floor((s % 3600) / 60));
    units.s.textContent = pad(s % 60);
    el.hidden = false;
  };
  const timer = setInterval(tick, 1000);
  tick();
}

/* ------------------------------------------------------------------ */
/* Cuenta atrás del carrito lateral (snippets/menora-cart-timer)       */
/* ------------------------------------------------------------------ */

// Recordatorio visual: no reserva nada. El inicio se guarda en localStorage para que no se reinicie
// al cerrar el panel, recargar ni abrir otra pestaña; solo se borra cuando el carrito queda vacío.
const CART_TIMER_KEY = 'mn-cart-timer-start';
const readCartTimerStart = () => {
  try {
    return parseInt(localStorage.getItem(CART_TIMER_KEY) || '', 10) || 0;
  } catch (e) {
    return 0;
  }
};
const writeCartTimerStart = (value) => {
  try {
    if (value) localStorage.setItem(CART_TIMER_KEY, String(value));
    else localStorage.removeItem(CART_TIMER_KEY);
  } catch (e) {}
};

class MenoraCartClock extends HTMLElement {
  connectedCallback() {
    // Los números van en shadow DOM: el repintado del carrito (morph) no los toca.
    if (!this.shadowRoot) this.attachShadow({ mode: 'open' });
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  tick() {
    const total = parseInt(this.dataset.seconds || '300', 10) || 300;
    const box = this.closest('[data-mn-cart-timer]');
    let start = readCartTimerStart();
    // Empieza la primera vez que el cliente ve el carrito con productos.
    if (!start && this.closest('dialog')?.open) {
      start = Date.now();
      writeCartTimerStart(start);
    }
    const left = start ? total - Math.floor((Date.now() - start) / 1000) : total;
    if (left <= 0) {
      // Fin: desaparece y no vuelve a empezar hasta que haya un carrito nuevo.
      clearInterval(this.timer);
      if (box) box.hidden = true;
      return;
    }
    const pad = (n) => String(n).padStart(2, '0');
    this.shadowRoot.textContent = `${pad(Math.floor(left / 60))}:${pad(left % 60)}`;
    if (box) box.hidden = false;
  }
}
if (!customElements.get('mn-cart-clock')) customElements.define('mn-cart-clock', MenoraCartClock);

class MenoraCartTimerReset extends HTMLElement {
  connectedCallback() {
    writeCartTimerStart(0);
  }
}
if (!customElements.get('mn-cart-timer-reset')) customElements.define('mn-cart-timer-reset', MenoraCartTimerReset);

/* ------------------------------------------------------------------ */
/* Reseñas: "ver más" y "leer completa"                                */
/* ------------------------------------------------------------------ */

function setupReviews(section) {
  const grid = section.querySelector('[data-mn-loadmore]');
  if (!grid || grid.dataset.mnReady) return;
  grid.dataset.mnReady = 'true';
  const filter = section.querySelector('[data-mn-review-filter]');
  filter?.addEventListener('click', () => {
    const on = filter.getAttribute('aria-pressed') !== 'true';
    filter.setAttribute('aria-pressed', String(on));
    grid.classList.toggle('is-photo-only', on);
  });
  // Como la referencia: se muestran N al principio y se añaden `step` por pulsación.
  // Con el filtro "solo con foto" activo, solo cuentan las reseñas con foto.
  const step = parseInt(grid.dataset.step || '6', 10);
  const btn = section.querySelector('[data-mn-loadmore-btn]');
  const photoOnly = () => grid.classList.contains('is-photo-only');
  const pending = () =>
    [...grid.querySelectorAll('.mn-review.is-hidden')].filter((el) => !photoOnly() || el.dataset.hasPhoto === 'true');
  const syncButton = () => {
    if (btn) btn.hidden = pending().length === 0;
  };
  btn?.addEventListener('click', () => {
    pending()
      .slice(0, step)
      .forEach((el) => {
        el.classList.remove('is-hidden');
        el.classList.add('is-revealed');
      });
    syncButton();
  });
  filter?.addEventListener('click', syncButton);
  syncButton();
  grid.addEventListener('click', (event) => {
    const more = event.target.closest('[data-mn-expand]');
    if (!more) return;
    const card = more.closest('.mn-review');
    const expanded = card.classList.toggle('is-expanded');
    more.textContent = expanded ? 'Mostrar menos' : 'Leer reseña completa';
  });
}

/* ------------------------------------------------------------------ */
/* Pie de página: abierto en escritorio, desplegable en móvil          */
/* ------------------------------------------------------------------ */

const desktop = window.matchMedia('(min-width: 990px)');
function syncFooter() {
  document.querySelectorAll('[data-mn-footer-col]').forEach((d) => {
    if (desktop.matches) d.setAttribute('open', '');
    else if (!d.dataset.mnTouched) d.removeAttribute('open');
  });
}
document.addEventListener(
  'click',
  (event) => {
    const summary = event.target.closest('[data-mn-footer-col] > summary');
    if (summary) summary.parentElement.dataset.mnTouched = 'true';
  },
  true
);
desktop.addEventListener?.('change', syncFooter);

/* ------------------------------------------------------------------ */
/* Arranque (y re-arranque en el editor de temas)                      */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* Carruseles automáticos continuos ([data-mn-marquee])                */
/* La animación es CSS; aquí solo se pausa al tocar / pasar el ratón.  */
/* ------------------------------------------------------------------ */

function setupMarquee(el) {
  if (el.dataset.mnReady) return;
  el.dataset.mnReady = 'true';
  const pause = () => el.classList.add('is-paused');
  const resume = () => el.classList.remove('is-paused');
  el.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && pause());
  el.addEventListener('pointerleave', (e) => e.pointerType === 'mouse' && resume());
  el.addEventListener('touchstart', pause, { passive: true });
  el.addEventListener('touchend', () => setTimeout(resume, 1200), { passive: true });
}

/* ------------------------------------------------------------------ */
/* Ventanas (tabla nutrimental)                                        */
/* ------------------------------------------------------------------ */

// La imagen de la tabla pesa: se empieza a descargar en cuanto se muestra intención de abrirla.
function warmDialog(opener) {
  const dialog = document.getElementById(opener.dataset.mnDialogOpen);
  dialog?.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
  return dialog;
}
['pointerenter', 'touchstart', 'focusin'].forEach((type) =>
  document.addEventListener(
    type,
    (event) => {
      const opener = event.target instanceof Element && event.target.closest('[data-mn-dialog-open]');
      if (opener) warmDialog(opener);
    },
    { capture: true, passive: true }
  )
);

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-mn-dialog-open]');
  if (opener) {
    const dialog = warmDialog(opener);
    if (dialog?.showModal) dialog.showModal();
    return;
  }
  if (event.target.closest('[data-mn-dialog-close]')) {
    event.target.closest('dialog')?.close();
    return;
  }
  // Clic en el fondo de la ventana: cerrar.
  if (event.target instanceof HTMLDialogElement && event.target.classList.contains('mn-dialog')) event.target.close();

  // "Ver más" en opiniones.
  const clampBtn = event.target.closest('[data-mn-clamp-toggle]');
  if (clampBtn) {
    const card = clampBtn.closest('.mn-tcard');
    const open = card.classList.toggle('is-open');
    clampBtn.textContent = open ? 'Ver menos' : 'Ver más';
  }
});

/* ------------------------------------------------------------------ */
/* Desplegables con apertura animada ([data-mn-accordion], .mn-accordion__item) */
/* ------------------------------------------------------------------ */

document.addEventListener('click', (event) => {
  const summary = event.target.closest('.mn-faq__item > summary, .mn-accordion__item > summary');
  if (!summary || prefersReducedMotion()) return;
  const details = summary.parentElement;
  const content = summary.nextElementSibling;
  if (!content || !content.animate) return;
  event.preventDefault();
  if (details.open) {
    const anim = content.animate([{ height: `${content.offsetHeight}px`, opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 220, easing: 'ease-out' });
    anim.onfinish = () => details.removeAttribute('open');
  } else {
    details.setAttribute('open', '');
    const h = content.offsetHeight;
    content.animate([{ height: '0px', opacity: 0 }, { height: `${h}px`, opacity: 1 }], { duration: 260, easing: 'ease-out' });
  }
});

function init(scope = document) {
  scope.querySelectorAll('[data-mn-carousel]').forEach(setupCarousel);
  scope.querySelectorAll('[data-mn-countdown]').forEach(setupCountdown);
  scope.querySelectorAll('[data-mn-marquee]').forEach(setupMarquee);
  scope.querySelectorAll('.mn-reviews').forEach(setupReviews);
  syncFooter();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
else init();

document.addEventListener('shopify:section:load', (event) => init(event.target));
